import React, { useLayoutEffect, useState } from 'react';
import './DeliveredLetterTrail.css';

/*
 * DeliveredLetterTrail
 * The one piece of storytelling in the hero: "the courier delivered Adiba's
 * introduction." A tiny envelope leaves a postmark near the outer edge of the
 * hero copy, travels a short dotted route up through the negative space
 * between the two columns, and settles just under the courier perched at the
 * portrait frame.
 *
 * It lives entirely in the empty corridor between the copy column and the
 * portrait (see .hero-trail in Hero.css) and is only rendered on viewports
 * wide enough for that corridor to exist, so it can never cross text,
 * buttons, the availability line or the photograph.
 *
 * Motion: the envelope makes the trip exactly once and stays where it lands.
 * Nothing loops. The whole sequence is over in about two seconds. The scene
 * is *drawn* in its delivered state and the animation is only opted into when
 * the document is visible at mount, so a hidden tab — where animations are
 * created but never advanced — simply shows the letter already delivered.
 * Under prefers-reduced-motion the same end state appears immediately.
 *
 * Purely decorative: aria-hidden, no pointer events.
 */

/* the route, shared by the drawn path and the envelope's motion path */
const TRAIL_D = 'M14 192C24 164 18 138 40 116 62 94 76 62 90 30';

function DeliveredLetterTrail({ className = '' }) {
  const [play, setPlay] = useState(false);

  useLayoutEffect(() => {
    if (typeof document === 'undefined' || document.hidden) return;
    setPlay(true);
  }, []);

  return (
    <svg
      className={`hero-trail ${play ? 'hero-trail--play' : ''} ${className}`}
      width="104"
      height="224"
      viewBox="0 0 104 224"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      {/* posted here — a small postmark ring where the letter starts out */}
      <circle
        className="hero-trail-mark hero-trail-mark--postmark"
        cx="14"
        cy="192"
        r="7.5"
        fill="none"
        stroke="#7D9A73"
        strokeWidth="1.5"
        strokeDasharray="2 3.4"
      />

      {/* the route itself */}
      <path
        className="hero-trail-ink"
        d={TRAIL_D}
        stroke="#C79A4E"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeDasharray="1.6 9"
      />

      {/* a folded note dropped along the way */}
      <g className="hero-trail-mark hero-trail-mark--note" transform="translate(54 99) rotate(-9)">
        <path
          d="M0 0h8.5l4 4v11H0z"
          fill="#FFFDF8"
          stroke="#7D9A73"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
        <path d="M8.5 0v4h4" fill="none" stroke="#7D9A73" strokeWidth="1.2" />
      </g>

      {/* a soft gold sparkle just before the hand-off */}
      <path
        className="hero-trail-mark hero-trail-mark--sparkle"
        transform="translate(68 46)"
        d="M0-5.5C.7-1.5 1.5-.7 5.5 0 1.5.7.7 1.5 0 5.5-.7 1.5-1.5.7-5.5 0-1.5-.7-.7-1.5 0-5.5z"
        fill="#D6A65E"
      />

      {/* the delivered letter: authored around its own origin so the motion
          path places it, drawn with a slight tilt so it reads as hand-carried */}
      <g className="hero-trail-envelope" style={{ offsetPath: `path("${TRAIL_D}")` }}>
        <g transform="rotate(-7)">
          <rect
            x="-8"
            y="-5.5"
            width="16"
            height="11"
            rx="1.8"
            fill="#FFFDF8"
            stroke="#4C6044"
            strokeWidth="1.4"
          />
          <path
            d="M-7.4-4.6 0 .9 7.4-4.6"
            fill="none"
            stroke="#B9863C"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      </g>
    </svg>
  );
}

export default DeliveredLetterTrail;
